/** Pure delivery calculation. Presentation, local holiday rules and Shopify integration live elsewhere. */
(() => {
  'use strict';
  const calendar = typeof module !== 'undefined' && module.exports
    ? require('./delivery-calendar.js') : window.DeliveryCalendar;
  function integer(value, name, minimum = 0) {
    if (!Number.isInteger(value) || value < minimum || value > 180) throw new Error(`Invalid ${name}`);
    return value;
  }
  function weekdays(values) {
    if (!Array.isArray(values) || !values.length || values.some(day => !/^[0-6]$/.test(String(day)))) {
      throw new Error('Select at least one valid weekday');
    }
    return new Set(values.map(Number));
  }
  function clock(now, timezone) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).map(part => [part.type, part.value]));
    return { date: calendar.date(`${parts.year}-${parts.month}-${parts.day}`), minutes: Number(parts.hour) * 60 + Number(parts.minute) };
  }
  function isWorking(day, days, holidays, blocked) {
    const key = calendar.iso(day);
    return days.has(day.getUTCDay()) && !holidays.has(key) && !blocked.has(key);
  }
  function nextWorking(day, days, holidays, blocked) {
    for (let guard = 0; guard < 730; guard++, day = calendar.add(day, 1)) {
      if (isWorking(day, days, holidays, blocked)) return day;
    }
    throw new Error('No working day within the supported horizon');
  }
  function advance(day, count, days, holidays, blocked) {
    for (let i = 0; i < count; i++) day = nextWorking(calendar.add(day, 1), days, holidays, blocked);
    return day;
  }
  function calculate(profile, rule, now, productionHolidays, deliveryHolidays) {
    const production = integer(profile.productionDays, 'production days');
    const min = integer(rule.totalMin, 'minimum delivery days', 1);
    const max = integer(rule.totalMax, 'maximum delivery days', 1);
    if (min > max || production > min) throw new Error('Delivery range must include the production duration');
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(profile.cutoff)) throw new Error('Invalid cutoff time');
    const productionDays = weekdays(profile.productionWeekdays), deliveryDays = weekdays(rule.deliveryWeekdays);
    const productionBlocked = new Set((profile.blockedDates || []).map(day => calendar.iso(calendar.date(day))));
    const deliveryBlocked = new Set((rule.blockedDates || []).map(day => calendar.iso(calendar.date(day))));
    // Empty optional vacation data preserves the existing blocked-date calculation.
    for (const range of profile.vacations || []) {
      const from = calendar.date(range.from), until = calendar.date(range.until);
      if (from > until || until > calendar.add(from, 3660)) throw new Error('Invalid vacation range');
      for (let day = from; day <= until; day = calendar.add(day, 1)) productionBlocked.add(calendar.iso(day));
    }
    const local = clock(now, profile.timezone);
    const [hours, minutes] = profile.cutoff.split(':').map(Number);
    const candidate = local.minutes >= hours * 60 + minutes ? calendar.add(local.date, 1) : local.date;
    const start = nextWorking(candidate, productionDays, productionHolidays, productionBlocked);
    const dispatch = advance(start, production, productionDays, productionHolidays, productionBlocked);
    // F-57, owner decision 20.10.2026: before cutoff today's order is the
    // first production day for the earliest dispatch; the upper bound keeps
    // the next-day allowance. Zero production days use the same start for both.
    const earliestDispatch = advance(start, Math.max(0, production - 1), productionDays, productionHolidays, productionBlocked);
    let delay = integer(rule.extraDays, 'additional delay');
    if (rule.temporaryEnabled) {
      const from = rule.delayFrom ? calendar.iso(calendar.date(rule.delayFrom)) : null;
      const until = rule.delayUntil ? calendar.iso(calendar.date(rule.delayUntil)) : null;
      if (from && until && from > until) throw new Error('Delay end precedes delay start');
      const today = calendar.iso(local.date);
      if ((!from || today >= from) && (!until || today <= until)) delay += integer(rule.temporaryDays, 'temporary delay');
    }
    if (max + delay > 180) throw new Error('Delivery horizon exceeds 180 working days');
    const arrival = (day, count) => nextWorking(advance(day, count + delay, deliveryDays, deliveryHolidays, deliveryBlocked), deliveryDays, deliveryHolidays, deliveryBlocked);
    const first = arrival(earliestDispatch, min - production), last = arrival(dispatch, max - production);
    if (last > calendar.add(local.date, 730)) throw new Error('Delivery exceeds the loaded calendar horizon');
    return { min: calendar.iso(first), max: calendar.iso(last), dispatch: calendar.iso(dispatch) };
  }
  const api = { calculate, clock };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    api.calculateCentral = require('../tools/delivery-central-test-adapter.cjs');
  }
  else window.DeliveryEngine = window.DeliveryEngine || api;
})();
